import { api, type User } from './api'

// Server-backed tenant UI config (Emerson 2026-08-13).
//
// The wordmark / font-group / nav-colour config authored on the QA page is
// stored on the tenant's Client row (`Client.ui_config`) so EVERY user of the
// tenant sees what the brander set — not just the browser that set it. Before
// this, productName.ts + navColor.ts persisted to localStorage only, so
// Emerson's QA settings never reached Heather/Maria (they saw code defaults).
//
//   precedence on load:  server tenant config  →  localStorage  →  code defaults
//
// This module owns only the HTTP + the client id / write-permission / last-
// known blob. Applying the fetched config into the productName/navColor stores
// happens in App.tsx (which imports those stores) to keep this module free of
// an import cycle; pushing happens from saveProductName / saveNavColor.

export type TenantUiConfig = {
  product_name?: unknown; nav_color?: unknown; nav_banner?: unknown
  // Per-tenant theme (Ken 2026-10-22): a map of CSS-var name → value, applied
  // inline on :root by applyServerTheme. Promotes the shared-code "no-key" marks
  // (hairline, pane bg, dividers, hover/selected, text colours, count colour,
  // focus ring) to per-tenant keys. A tenant without this key renders unchanged.
  theme?: unknown
  // Pane-1 options (Ken 2026-10-23). category_band_style flips the category label
  // between the filled pill ("filled") and a bare label ("none"); both band colour
  // tokens stay defined in the theme either way, so flipping is one value, no rebuild.
  pane1?: { category_band_style?: 'none' | 'filled' }
  agreement_queue_glyph?: unknown; ready_for_drafting_glyph?: unknown
  // Glyph registry (Ken 2026-10-22). One entry per named UI glyph:
  //   { label, filename, media }  — media is a data URI (null until uploaded).
  // The <Glyph> component reads this; a tenant swap updates every instance.
  glyphs?: unknown
  // Uploaded custom fonts (Ken 2026-10-27). A map of { familyName: dataURI }.
  // applyUploadedFonts injects one @font-face per entry so the family name
  // becomes usable anywhere (theme vars, voice assignments). A tenant without
  // this key (e.g. MOVU) registers nothing. Per-tenant; cleared on act-as.
  fonts?: unknown
  // Named font-setting sets (Ken 2026-10-28): { setName: { --me-font-*: value } }.
  // Each is a complete snapshot of the font theme vars; save/apply via the
  // getFontSets/saveFontSet/applyFontSet helpers. Does not itself change render.
  font_sets?: unknown
}

// One registry entry for a named glyph. All optional — the frontend GLYPH_DEFAULTS
// supply label/filename when the tenant hasn't overridden, and `media` stays null
// until Claude Desktop's art is pulled in.
export type GlyphEntry = { label?: string; filename?: string; media?: string | null }

export function getGlyphEntry(name: string): GlyphEntry | null {
  const all = (_server as Record<string, unknown>).glyphs
  if (!all || typeof all !== 'object') return null
  const e = (all as Record<string, unknown>)[name]
  return e && typeof e === 'object' ? (e as GlyphEntry) : null
}

let _clientId: number | null = null
let _canWrite = false
let _server: TenantUiConfig = {}
let _intakeAddress = ''  // Client.contract_intake_address (a top-level field, not ui_config)

function computeCanWrite(user: User): boolean {
  // Mirror the backend ClientViewSet._can_write_client gate: admin /
  // studio_editor / superuser (NOT studio_viewer, NOT plain tenant users).
  return !!(user.is_superuser || user.role === 'admin' || user.role === 'studio_editor')
}

// Resolve the caller's tenant client id. Tenant users carry it on the auth
// user; a superuser with no direct client (e.g. Emerson signed in as himself)
// falls back to the clients list, which is single-tenant on dev-2.
async function resolveClientId(user: User): Promise<number | null> {
  if (user.client?.id) return user.client.id
  try {
    const r = await api.get<Array<{ id: number; code: string }>>('/clients/')
    if (Array.isArray(r.data) && r.data.length) return r.data[0].id
  } catch { /* ignore */ }
  return null
}

// Fetch the tenant's ui_config and prime this module. Returns the blob so the
// caller (App) can apply it into the productName/navColor stores. Safe to call
// on every authenticated mount; never throws.
export async function initUiConfigSync(user: User): Promise<TenantUiConfig> {
  _canWrite = computeCanWrite(user)
  _clientId = await resolveClientId(user)
  if (_clientId == null) { _server = {}; notifyConfigLoaded(); return _server }
  try {
    const r = await api.get<{ ui_config?: TenantUiConfig; contract_intake_address?: string }>(`/clients/${_clientId}/`)
    _server = (r.data && r.data.ui_config) || {}
    _intakeAddress = (r.data && r.data.contract_intake_address) || ''
  } catch {
    _server = {}
  }
  // The config arrives asynchronously, after QA panels have already mounted.
  // Notify so snapshot-based panels (e.g. the font list) re-read _server once
  // it's populated, instead of showing the empty pre-fetch state (Ken 2026-10-27).
  notifyConfigLoaded()
  return _server
}

// Minimal pub/sub so QA panels can re-read module state after the async config
// load (and after local push/remove). Fires on initUiConfigSync completion.
const _configListeners = new Set<() => void>()
export function subscribeConfigLoaded(fn: () => void): () => void {
  _configListeners.add(fn)
  return () => { _configListeners.delete(fn) }
}
function notifyConfigLoaded(): void {
  _configListeners.forEach((fn) => { try { fn() } catch { /* ignore */ } })
}

// Dedicated contract-intake address (Client.contract_intake_address) — the address a
// submission must name in To/Cc to be treated as a contract submission. A top-level
// field, so it's PATCHed directly (not via ui_config). (Ken 2026-10-12)
export function getContractIntakeAddress(): string { return _intakeAddress }

export function saveContractIntakeAddress(addr: string): void {
  _intakeAddress = addr
  if (_canWrite && _clientId != null) {
    api.patch(`/clients/${_clientId}/`, { contract_intake_address: addr }).catch(() => { /* local cache still works */ })
  }
}

// Debounced PATCH so rapid QA edits (slider drags, per-keystroke wordmark
// typing) coalesce into one write. The caller already updated localStorage
// synchronously for instant local feedback; this only syncs the server.
let _timer: ReturnType<typeof setTimeout> | null = null
function schedulePatch(): void {
  if (!_canWrite || _clientId == null) return
  if (_timer) clearTimeout(_timer)
  const id = _clientId
  const body = { ui_config: _server }
  _timer = setTimeout(() => {
    _timer = null
    api.patch(`/clients/${id}/`, body).catch(() => { /* ignore — local cache still works */ })
  }, 600)
}

export function pushProductName(cfg: unknown): void {
  _server = { ..._server, product_name: cfg }
  schedulePatch()
}

export function pushNavColor(color: string): void {
  _server = { ..._server, nav_color: color }
  schedulePatch()
}

// Active control-bar banner as a data URL. Passing null/'' drops the key so
// the stored ui_config has no banner (header falls back to nav_color). The
// re-selectable history lives server-side in NavBanner, not here — only the
// ACTIVE image rides the tenant config every user loads.
export function pushNavBanner(dataUrl: string | null): void {
  _server = { ..._server, nav_banner: dataUrl || undefined }
  schedulePatch()
}

// Custom glyphs (small PNG/SVG) for workflow buttons, authored on QA and stored on the
// tenant config so every user sees them. Keyed, so one helper serves many buttons
// (Agreement Queue, Ready for Drafting, …). Null/'' drops the key. (Ken 2026-10-12)
export function pushGlyph(key: string, dataUrl: string | null): void {
  _server = { ..._server, [key]: dataUrl || undefined }
  schedulePatch()
}

export function getGlyph(key: string): string | null {
  const g = (_server as Record<string, unknown>)[key]
  return typeof g === 'string' && g ? g : null
}

// Pane-1 category band style (Ken 2026-10-23): 'none' = bare label, 'filled' =
// the pill. Default 'filled' (unset tenants, incl. MOVU, are unchanged).
export function getCategoryBandStyle(): 'none' | 'filled' {
  const p = (_server as Record<string, unknown>).pane1
  const v = p && typeof p === 'object' ? (p as { category_band_style?: unknown }).category_band_style : undefined
  return v === 'none' ? 'none' : 'filled'
}

// Back-compat alias used by the Agreement Queue button.
export function getAgreementQueueGlyph(): string | null { return getGlyph('agreement_queue_glyph') }

// Per-tenant theme (Ken 2026-10-22). The overridable CSS vars — listed so we can
// CLEAR a prior tenant's overrides when switching tenants (act-as) or when the
// current tenant has no theme (e.g. MOVU), reverting to the index.css :root
// defaults. Keep in sync with the themeable vars in index.css.
const THEME_VARS = [
  '--me-bg', '--me-surface', '--me-panel', '--me-border', '--me-borderStrong',
  '--me-text', '--me-textDim', '--me-textMute', '--me-hover', '--me-selected',
  '--me-hairline-color', '--me-hairline-w', '--me-divider-color', '--me-divider-w',
  '--me-count-color', '--me-quiet-btn-text', '--me-focus-ring',
  '--me-surface-1', '--me-surface-2', '--me-surface-3', '--me-accent-select',
  '--me-accent-fg', '--me-accent-bg', '--me-accent-line',
  '--me-band-fill', '--me-band-label',
  '--me-font-category', '--me-font-category-size', '--me-font-title', '--me-font-stem',
  '--me-font-chrome-label', '--me-font-chrome-control', '--me-font-chrome-value',
  '--me-font-nav', '--me-font-ai-header', '--me-font-folder-loc',
  '--me-font-refile', '--me-font-doc-toggle',
  '--me-font-matter-name', '--me-font-summary-body', '--me-font-subject', '--me-font-record-body',
  '--me-font-recipients', '--me-font-recipient-label', '--me-font-recipient-date',
  // Six voice slots + AI-chat pieces (Ken 2026-10-27).
  '--me-font-system-1', '--me-font-system-2', '--me-font-ground-1', '--me-font-ground-2',
  '--me-font-gen-1', '--me-font-gen-2',
  '--me-font-ai-title', '--me-font-chat-answer', '--me-font-chat-question', '--me-font-citation',
  // Gen-3 + per-voice weight/size/case knobs (Ken 2026-10-28).
  '--me-font-gen-3', '--me-font-weight-gen-3', '--me-font-size-ground-1', '--me-font-case-title',
  // Ground-3 / System-3 / client-activity + body bg/colour (Ken 2026-10-28).
  '--me-font-ground-3', '--me-font-ground-3-color', '--me-font-record-body-bg',
  '--me-font-system-3', '--me-font-client-activity', '--me-font-recipient-color',
]

// Apply a tenant's theme map as inline overrides on :root, and flag [data-themed]
// (the focus-ring rule keys off it). Passing null/empty clears all overrides and
// the flag — so MOVU (no theme) is byte-for-byte the index.css defaults, and an
// act-as switch away from KEN cleanly reverts. Only '--me-*' string entries are
// honoured (defensive against junk in the JSON).
export function applyServerTheme(theme: unknown): void {
  const root = document.documentElement
  for (const v of THEME_VARS) root.style.removeProperty(v)
  const entries = theme && typeof theme === 'object'
    ? Object.entries(theme as Record<string, unknown>) : []
  const valid = entries.filter(([k, v]) => k.startsWith('--me-') && typeof v === 'string') as [string, string][]
  if (valid.length === 0) { root.removeAttribute('data-themed'); return }
  for (const [k, v] of valid) root.style.setProperty(k, v)
  root.setAttribute('data-themed', 'on')
}

export function pushTheme(theme: Record<string, string> | null): void {
  _server = { ..._server, theme: theme || undefined }
  schedulePatch()
}

export function getTheme(): Record<string, string> {
  const t = (_server as Record<string, unknown>).theme
  return t && typeof t === 'object' ? (t as Record<string, string>) : {}
}

// ── Named font-setting sets (Ken 2026-10-28) ────────────────────────────────
// A "set" is a named snapshot of every --me-font-* theme var (the six voice
// slots + each element's slot mapping). Lets a brander keep several complete
// font looks and switch between them. Stored on ui_config.font_sets; a future
// Studio UI drives save/apply — the capability lives here now so it's ready.
export function getFontSets(): Record<string, Record<string, string>> {
  const s = (_server as Record<string, unknown>).font_sets
  return s && typeof s === 'object' ? (s as Record<string, Record<string, string>>) : {}
}

export function listFontSets(): string[] { return Object.keys(getFontSets()) }

// Snapshot the current theme's font vars under `name` (overwrites a same-named set).
export function saveFontSet(name: string): void {
  const theme = getTheme()
  const snap: Record<string, string> = {}
  for (const [k, v] of Object.entries(theme)) if (k.startsWith('--me-font') && typeof v === 'string') snap[k] = v
  const sets = { ...getFontSets(), [name]: snap }
  _server = { ..._server, font_sets: sets }
  schedulePatch()
}

// Merge a saved set's font vars into the active theme and apply it live.
export function applyFontSet(name: string): void {
  const snap = getFontSets()[name]
  if (!snap) return
  const theme = { ...getTheme(), ...snap }
  pushTheme(theme)
  applyServerTheme(theme)
}

export function deleteFontSet(name: string): void {
  const sets = { ...getFontSets() }
  delete sets[name]
  _server = { ..._server, font_sets: Object.keys(sets).length ? sets : undefined }
  schedulePatch()
}

// ── Uploaded custom fonts (Ken 2026-10-27) ──────────────────────────────────
// Self-hosted web fonts a brander uploads in Studio, stored as data URIs on the
// tenant config (same shape as glyphs/logo). getFontsMap / pushFont / removeFont
// own the config side; applyUploadedFonts registers them as @font-face so the
// family name is usable in any font-family value (theme vars, future voices).

export function getFontsMap(): Record<string, string> {
  const f = (_server as Record<string, unknown>).fonts
  return f && typeof f === 'object' ? (f as Record<string, string>) : {}
}

export function pushFont(name: string, dataUrl: string): void {
  const fonts = { ...getFontsMap(), [name]: dataUrl }
  _server = { ..._server, fonts }
  schedulePatch()
}

export function removeFont(name: string): void {
  const fonts = { ...getFontsMap() }
  delete fonts[name]
  _server = { ..._server, fonts: Object.keys(fonts).length ? fonts : undefined }
  schedulePatch()
}

// Infer the CSS src format() hint from a data URI's MIME type. Returns '' when
// unknown, in which case we omit the hint and let the browser sniff.
function fontFormat(src: string): string {
  const mime = (/^data:([^;,]*)/.exec(src)?.[1] || '').toLowerCase()
  if (mime.includes('woff2')) return 'woff2'
  if (mime.includes('woff')) return 'woff'
  if (mime.includes('ttf') || mime.includes('truetype')) return 'truetype'
  if (mime.includes('otf') || mime.includes('opentype')) return 'opentype'
  return ''
}

// Replace the <style id="me-uploaded-fonts"> block with one @font-face per
// uploaded font. Idempotent: passing null/empty removes the block entirely, so
// an act-as switch away from a font-carrying tenant (or MOVU, with none) leaves
// nothing registered. Only 'data:' values are honoured (defensive against junk).
export function applyUploadedFonts(fonts: unknown): void {
  const id = 'me-uploaded-fonts'
  let el = document.getElementById(id) as HTMLStyleElement | null
  const entries = (fonts && typeof fonts === 'object'
    ? Object.entries(fonts as Record<string, unknown>) : [])
    .filter(([k, v]) => typeof k === 'string' && typeof v === 'string' && (v as string).startsWith('data:')) as [string, string][]
  if (entries.length === 0) { if (el) el.remove(); return }
  if (!el) { el = document.createElement('style'); el.id = id; document.head.appendChild(el) }
  el.textContent = entries.map(([name, src]) => {
    const fam = name.replace(/['\\]/g, '')  // keep the family-name string safe
    const fmt = fontFormat(src)
    const srcVal = fmt ? `url("${src}") format("${fmt}")` : `url("${src}")`
    return `@font-face{font-family:'${fam}';src:${srcVal};font-display:swap;}`
  }).join('\n')
}
